// src/components/review/ProposalReviewList.js
// MVP da tab "Propostas AI" do ReviewWorkbench.
// Lista ai_proposals(review_status='pending') com approve/reject otimista.
//
// Decisões MVP:
//   - sem edição antes de aprovar
//   - bulk actions via useBulkSelection + BulkActionBar (chunks de 50)
//   - sem criação de knowledge_atoms (responsabilidade do backend, vem depois)
//   - reviewed_by hardcoded='eduardo' até integrar auth
//   - optimistic remove + rollback em caso de erro (single action)
//   - bulk: re-load do banco em falha parcial (consistência > otimismo)
//   - toast inferior-direito de 2.5s

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check, X, Sparkles, MessageCircle, Mail, Phone, AlertCircle,
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import useBulkSelection from '../shared/useBulkSelection';
import BulkActionBar from '../shared/BulkActionBar';
import SelectAllCheckbox from '../shared/SelectAllCheckbox';
import FilterChips from '../shared/FilterChips';

// Fluxo do filtro:
//   Usuário clica chip "price (2)" → lista mostra 2 propostas de preço
//   → SelectAll → 2 selecionadas → BulkActionBar
//   → Aprovar selecionados → confirm cauteloso → 2 aprovadas → filtro volta "Todos".

const REVIEWED_BY = 'eduardo'; // MVP — sem auth
const BULK_CHUNK = 50;
const ALL_FILTER = '__all';

// Mapeia category → color token do FilterChips (espelha CATEGORY_TONE abaixo)
const CATEGORY_COLOR = {
  price:            'emerald',
  commitment:       'blue',
  condition:        'orange',
  deadline:         'amber',
  contact_info:     'purple',
  contact_mention:  'purple',
  entity_reference: 'indigo',
  action_item:      'cyan',
  relationship:     'pink',
  location:         'teal',
  discovery:        'violet',
  other:            'slate',
};

const CATEGORY_TONE = {
  price:            'bg-emerald-100 text-emerald-800 border-emerald-200',
  commitment:       'bg-blue-100    text-blue-800    border-blue-200',
  condition:        'bg-orange-100  text-orange-800  border-orange-200',
  deadline:         'bg-amber-100   text-amber-800   border-amber-200',
  contact_info:     'bg-purple-100  text-purple-800  border-purple-200',
  contact_mention:  'bg-purple-100  text-purple-800  border-purple-200',
  entity_reference: 'bg-indigo-100  text-indigo-800  border-indigo-200',
  action_item:      'bg-cyan-100    text-cyan-800    border-cyan-200',
  relationship:     'bg-pink-100    text-pink-800    border-pink-200',
  location:         'bg-teal-100    text-teal-800    border-teal-200',
  discovery:        'bg-violet-100  text-violet-800  border-violet-200',
  other:            'bg-slate-100   text-slate-700   border-slate-200',
};

const CHANNEL_META = {
  whatsapp:   { icon: MessageCircle, label: 'WhatsApp', color: 'text-emerald-600' },
  email:      { icon: Mail,          label: 'Email',    color: 'text-blue-600' },
  phone_call: { icon: Phone,         label: 'Chamada',  color: 'text-amber-600' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function fmtPhone(raw) {
  if (!raw) return '';
  const d = String(raw).replace(/\D+/g, '');
  if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  if (d.length === 13 && d.startsWith('55')) {
    return `(${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`;
  }
  return raw;
}

function fmtConfidence(c) {
  if (c == null) return '—';
  const n = Number(c);
  if (isNaN(n)) return '—';
  return `${Math.round(n * 100)}%`;
}

function confidenceTone(c) {
  const n = Number(c);
  if (isNaN(n)) return 'bg-slate-100 text-slate-600';
  if (n >= 0.85) return 'bg-emerald-100 text-emerald-700';
  if (n >= 0.7)  return 'bg-amber-100   text-amber-700';
  return 'bg-red-100 text-red-700';
}

export default function ProposalReviewList({ onChange }) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(ALL_FILTER);
  const sel = useBulkSelection();

  // Chips por proposed_data.category (cores espelham CATEGORY_TONE)
  const { filterChips, filteredProposals } = useMemo(() => {
    const counts = new Map();
    for (const p of proposals) {
      const cat = p.proposed_data?.category || 'other';
      counts.set(cat, (counts.get(cat) || 0) + 1);
    }
    const chips = [
      { key: ALL_FILTER, label: 'Todos', count: proposals.length },
      ...Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([cat, n]) => ({
          key: cat,
          label: cat,
          count: n,
          color: CATEGORY_COLOR[cat] || 'slate',
        })),
    ];
    const filtered = categoryFilter === ALL_FILTER
      ? proposals
      : proposals.filter((p) => (p.proposed_data?.category || 'other') === categoryFilter);
    return { filterChips: chips, filteredProposals: filtered };
  }, [proposals, categoryFilter]);

  const handleFilterChange = (key) => {
    setCategoryFilter(key);
    sel.deselectAll();
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('ai_proposals')
      .select(
        'id, proposal_type, source_table, source_id, source_channel, agent_name, ai_confidence, proposed_data, created_at',
      )
      .eq('review_status', 'pending')
      // entity_create tem tab própria (Empresas EREL) — decisão só via
      // fn_erel_review_proposal; o update direto daqui furaria o gate G3B
      .neq('proposal_type', 'entity_create')
      .order('created_at', { ascending: false });

    if (err) {
      setError(err.message);
      setProposals([]);
      setLoading(false);
      return;
    }

    // UX-7: linha de origem — join leve source_id → mensagens_whatsapp
    let rows = data || [];
    const msgIds = [...new Set(
      rows
        .filter((p) => p.source_id && (!p.source_table || p.source_table === 'mensagens_whatsapp'))
        .map((p) => p.source_id),
    )];
    if (msgIds.length > 0) {
      const { data: msgs, error: msgErr } = await supabase
        .from('mensagens_whatsapp')
        .select('id, telefone_contato, source_chat_label, data_envio')
        .in('id', msgIds);
      if (!msgErr) {
        const byId = new Map((msgs || []).map((m) => [m.id, m]));
        rows = rows.map((p) => ({ ...p, _origin: byId.get(p.source_id) || null }));
      }
    }
    setProposals(rows);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = useCallback((message, tone = 'success') => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 2500);
  }, []);

  const decide = useCallback(
    async (proposal, action) => {
      setBusyId(proposal.id);
      // optimistic remove
      const snapshot = proposals;
      setProposals((prev) => prev.filter((p) => p.id !== proposal.id));

      const { error: updErr } = await supabase
        .from('ai_proposals')
        .update({
          review_status: action === 'approved' ? 'approved' : 'rejected',
          reviewed_by: REVIEWED_BY,
          reviewed_at: new Date().toISOString(),
          human_decision: { action },
        })
        .eq('id', proposal.id);

      setBusyId(null);

      if (updErr) {
        // rollback
        setProposals(snapshot);
        showToast(`Erro ao salvar: ${updErr.message}`, 'error');
        return;
      }

      showToast(
        action === 'approved' ? 'Proposta aprovada' : 'Proposta rejeitada',
        'success',
      );
      if (typeof onChange === 'function') onChange();
    },
    [proposals, onChange, showToast],
  );

  // Bulk approve/reject — chunked, non-optimistic.
  // Em falha parcial: ressincroniza com banco via load() (sucesso já gravado fica;
  // os que não rodaram permanecem visíveis). Throw mantém seleção na BulkActionBar.
  const bulkDecide = async (action) => {
    const ids = sel.getSelectedIds();
    if (ids.length === 0) return;
    const reviewed_at = new Date().toISOString();

    for (let i = 0; i < ids.length; i += BULK_CHUNK) {
      const chunk = ids.slice(i, i + BULK_CHUNK);
      const { error: updErr } = await supabase
        .from('ai_proposals')
        .update({
          review_status: action,
          reviewed_by: REVIEWED_BY,
          reviewed_at,
          human_decision: { action, mode: 'bulk', count: ids.length },
        })
        .in('id', chunk);

      if (updErr) {
        showToast(`Erro ao salvar lote: ${updErr.message}`, 'error');
        await load();
        throw updErr; // BulkActionBar mantém seleção
      }
    }

    const idSet = new Set(ids);
    setProposals((prev) => prev.filter((p) => !idSet.has(p.id)));
    const verb = action === 'approved' ? 'aprovada' : 'rejeitada';
    showToast(`${ids.length} ${ids.length === 1 ? `proposta ${verb}` : `propostas ${verb}s`}`, 'success');
    if (typeof onChange === 'function') onChange();
  };

  const headerLabel = loading
    ? 'Carregando…'
    : `${proposals.length} proposta${proposals.length === 1 ? '' : 's'} pendente${proposals.length === 1 ? '' : 's'}`;

  return (
    <div className="space-y-3">
      {/* Tab legado: approve/reject aqui não cria conhecimento (reforma = G5) */}
      <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800">
        Tab legado (pré-G5): aprovar/rejeitar apenas muda o status — não cria conhecimento.
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-slate-600 font-medium">{headerLabel}</div>
        {filteredProposals.length > 0 && (
          <SelectAllCheckbox
            totalCount={filteredProposals.length}
            selectedCount={sel.count}
            onSelectAll={() => sel.selectAll(filteredProposals.map((p) => p.id))}
            onDeselectAll={sel.deselectAll}
          />
        )}
      </div>

      <FilterChips chips={filterChips} activeKey={categoryFilter} onChange={handleFilterChange} />

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-xs text-red-800 flex items-start gap-2">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && proposals.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <p className="text-sm text-slate-600">Nenhuma proposta pendente. 🎉</p>
          <p className="text-xs text-slate-400 mt-1">
            Todas as propostas geradas pela AI já foram revisadas.
          </p>
        </div>
      )}

      {!loading && !error && proposals.length > 0 && filteredProposals.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 text-center">
          <p className="text-xs text-slate-500">
            Nenhuma proposta neste filtro.{' '}
            <button
              onClick={() => handleFilterChange(ALL_FILTER)}
              className="text-blue-600 hover:underline"
            >
              Limpar filtro
            </button>
          </p>
        </div>
      )}

      {filteredProposals.map((p) => (
        <ProposalCard
          key={p.id}
          proposal={p}
          busy={busyId === p.id}
          selected={sel.isSelected(p.id)}
          onToggleSelect={() => sel.toggle(p.id)}
          onApprove={() => decide(p, 'approved')}
          onReject={() => decide(p, 'rejected')}
        />
      ))}

      <BulkActionBar
        count={sel.count}
        onClear={sel.deselectAll}
        actions={[
          {
            label: 'Aprovar selecionados',
            icon: Check,
            color: 'green',
            confirmMessage: (n) =>
              `Aprovar ${n} proposta${n === 1 ? '' : 's'} como conhecimento? Verifique se o filtro está correto.`,
            onClick: () => bulkDecide('approved'),
          },
          {
            label: 'Rejeitar selecionados',
            icon: X,
            color: 'red',
            confirmMessage: (n) => `Rejeitar ${n} proposta${n === 1 ? '' : 's'}?`,
            onClick: () => bulkDecide('rejected'),
          },
        ]}
      />

      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-50 px-3 py-2 rounded shadow-lg text-xs font-medium ${
            toast.tone === 'error'
              ? 'bg-red-600 text-white'
              : 'bg-emerald-600 text-white'
          }`}
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

function ProposalCard({ proposal, busy, selected, onToggleSelect, onApprove, onReject }) {
  const pd = proposal.proposed_data || {};
  const category = pd.category || 'other';
  const content = pd.content || '(sem conteúdo)';
  const excerpt = pd.source_excerpt;
  const tone = CATEGORY_TONE[category] || CATEGORY_TONE.other;
  const ch =
    CHANNEL_META[proposal.source_channel] || {
      icon: Sparkles,
      label: proposal.source_channel || 'desconhecido',
      color: 'text-slate-500',
    };
  const ChannelIcon = ch.icon;

  return (
    <div
      className={`bg-white border rounded-lg p-4 transition-opacity ${
        selected ? 'border-emerald-400 bg-emerald-50/40' : 'border-slate-200'
      } ${busy ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={!!selected}
          onChange={onToggleSelect}
          aria-label="Selecionar proposta"
          className="cursor-pointer w-5 h-5 mt-0.5 accent-emerald-600 flex-shrink-0 rounded"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span
              className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${tone}`}
            >
              {category}
            </span>
        <span
          className={`px-2 py-0.5 rounded text-[10px] font-semibold ${confidenceTone(
            proposal.ai_confidence,
          )}`}
          title="Confiança do agent"
        >
          {fmtConfidence(proposal.ai_confidence)}
        </span>
        <span className="flex items-center gap-1 text-[11px] text-slate-500">
          <ChannelIcon size={11} className={ch.color} />
          {ch.label}
        </span>
        <span className="text-[11px] text-slate-400">·</span>
        <span className="text-[11px] text-slate-500">{fmtDate(proposal.created_at)}</span>
        {proposal.agent_name && (
          <>
            <span className="text-[11px] text-slate-400">·</span>
            <span
              className="text-[10px] text-slate-400 font-mono"
              title="Agent que gerou a proposta"
            >
              {proposal.agent_name}
            </span>
          </>
        )}
      </div>

      <p className="text-sm text-slate-900 leading-snug mb-2">{content}</p>

          {/* UX-7: de qual conversa/contato o fato veio */}
          {proposal._origin && (
            <p className="text-[11px] text-slate-500 mb-2">
              Origem:{' '}
              <span className="font-medium text-slate-700">
                {proposal._origin.source_chat_label || fmtPhone(proposal._origin.telefone_contato) || '—'}
              </span>
              {proposal._origin.source_chat_label && proposal._origin.telefone_contato && (
                <span className="font-mono"> · {fmtPhone(proposal._origin.telefone_contato)}</span>
              )}
              {' · '}{fmtDate(proposal._origin.data_envio)}
            </p>
          )}

          {excerpt && (
            <blockquote className="border-l-2 border-slate-300 pl-3 text-xs text-slate-600 italic mb-3">
              "{excerpt}"
            </blockquote>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <button
          onClick={onReject}
          disabled={busy}
          className="px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-200 rounded hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5"
        >
          <X size={13} /> Rejeitar
        </button>
        <button
          onClick={onApprove}
          disabled={busy}
          className="px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
        >
          <Check size={13} /> Aprovar
        </button>
      </div>
    </div>
  );
}
